import { useUserContext } from '@/contexts/UserContext';
import { useAlert } from '@/hooks/useAlert';
import { useMultiTenantServices } from '@/services/supabase/entities';
import { useCallback, useEffect, useState } from 'react';

// Types específicos para gestão de créditos
interface SaldoResponsavel {
     id_responsavel: number | null;
     responsavel: string | null;
     email: string | null;
     organizacao: string | null;
     saldo_atual: number | null;
     saldo_bloqueado: number | null;
     saldo_disponivel: number | null;
     total_dependentes: number | null;
}

interface MovimentacaoCredito {
     id: number | null;
     pessoa: string | null;
     tipo_pessoa: string | null;
     tipo_movimentacao: string | null;
     tipo_origem: string | null;
     valor: number | null;
     saldo_anterior: number | null;
     saldo_posterior: number | null;
     descricao: string | null;
     data_movimentacao: string | null;
     criado_por: string | null;
     organizacao: string | null;
}

interface AdicionarCreditoParams {
     valor: number;
     descricao: string;
}

export const useCreditosData = () => {
     const { userData } = useUserContext();
     const { showSuccess, showError } = useAlert();

     // Estados locais
     const [saldo, setSaldo] = useState<SaldoResponsavel | null>(null);
     const [movimentacoes, setMovimentacoes] = useState<MovimentacaoCredito[]>([]);
     const [loading, setLoading] = useState(true);
     const [recarregando, setRecarregando] = useState(false);

     // Obter services de forma estável
     const services = useMultiTenantServices();

     // Função de carregamento inicial estável - sem dependências circulares
     const carregarDadosIniciais = useCallback(async () => {
          if (!userData?.id) {
               console.log('⚠️ userData ou id não disponível');
               setLoading(false);
               return;
          }

          setLoading(true);

          try {
               // Carregar saldo
               const saldoResponse = await services.viewSaldosResponsaveis.getSaldoPorResponsavel(userData.id);

               if (saldoResponse.data) {
                    // Se retornar array, pegar o primeiro item
                    const saldoData = Array.isArray(saldoResponse.data) ? saldoResponse.data[0] : saldoResponse.data;
                    setSaldo(saldoData);
               } else {
                    console.warn('⚠️ Nenhum saldo encontrado para o usuário');
                    setSaldo(null);
               }

               // Carregar movimentações
               const movResponse = await services.viewMovimentacoesRecentes.getMovimentacoesPorPessoa(userData.id);

               if (movResponse.data) {
                    const movimentacoesFlat = Array.isArray(movResponse.data[0]) ? movResponse.data.flat() : movResponse.data;
                    setMovimentacoes(movimentacoesFlat as MovimentacaoCredito[]);
               } else {
                    console.warn('⚠️ Nenhuma movimentação encontrada');
                    setMovimentacoes([]);
               }

               console.log('✅ Carregamento concluído com sucesso');
          } catch (error) {
               console.error('❌ Erro ao carregar dados:', error);
               showError('Erro ao carregar dados: ' + (error as Error).message);
               // Define dados vazios em caso de erro
               setSaldo(null);
               setMovimentacoes([]);
          } finally {
               setLoading(false);
          }
     }, [userData?.id, services, showError]);

     const recarregar = useCallback(async () => {
          setRecarregando(true);
          try {
               // Recarregar usando função interna sem setLoading para evitar conflito
               const saldoResponse = await services.viewSaldosResponsaveis.getSaldoPorResponsavel(userData?.id!);
               if (saldoResponse.data) {
                    const saldoData = Array.isArray(saldoResponse.data) ? saldoResponse.data[0] : saldoResponse.data;
                    setSaldo(saldoData);
               }

               const movResponse = await services.viewMovimentacoesRecentes.getMovimentacoesPorPessoa(userData?.id!);
               if (movResponse.data) {
                    const movimentacoesFlat = Array.isArray(movResponse.data[0]) ? movResponse.data.flat() : movResponse.data;
                    setMovimentacoes(movimentacoesFlat as MovimentacaoCredito[]);
               }

               showSuccess('Dados atualizados');
          } catch (error) {
               showError('Erro ao atualizar dados');
          } finally {
               setRecarregando(false);
          }
     }, [userData?.id, services, showSuccess, showError]);

     const adicionarCredito = useCallback(
          async (_params: AdicionarCreditoParams) => {
               return false;
          },
          [showError],
     );

     // Efeito para carregar dados iniciais - apenas quando userId muda
     useEffect(() => {
          if (userData?.id) {
               carregarDadosIniciais();
          }
     }, [userData?.id]); // Apenas userData?.id como dependência

     // Métricas calculadas
     const metricas = {
          saldoDisponivel: saldo?.saldo_disponivel ?? 0,
          saldoBloqueado: saldo?.saldo_bloqueado ?? 0,
          totalDependentes: saldo?.total_dependentes ?? 0,
          ultimaMovimentacao: movimentacoes.length > 0 ? movimentacoes[0] : null,
          totalMovimentacoesMes: movimentacoes.filter((m) => {
               if (!m.data_movimentacao) return false;
               const dataMovimentacao = new Date(m.data_movimentacao);
               const inicioMes = new Date();
               inicioMes.setDate(1);
               inicioMes.setHours(0, 0, 0, 0);
               return dataMovimentacao >= inicioMes;
          }).length,
     };

     // Metadados da página
     const metadados = {
          titulo: 'Carteira',
          subtitulo: 'Gerencie seu saldo e recargas via PIX',
          breadcrumbs: [
               { label: 'Início', path: '/' },
               { label: 'Gestão Financeira', path: '/gestao-financeira' },
               { label: 'Carteira', path: '/creditos' },
          ],
     };

     return {
          // Estados
          saldo,
          movimentacoes,
          loading,
          recarregando,

          // Ações
          adicionarCredito,
          recarregar,

          // Métricas
          metricas,

          // Metadados
          metadados,
     };
};

export type { AdicionarCreditoParams, MovimentacaoCredito, SaldoResponsavel };
